<template>
    <div>
        <b-row class="pt-2 pb-1">
            <b-col cols="6" class="d-flex align-items-center">
                <BreadcrumbMenu />
            </b-col>
        </b-row>
        <!-- Toastification loading content export -->
        <toastification-loading-content ref="loadingToast" />

        <!-- Table Container Card -->
        <b-card class="m-2" no-body>

            <div class="m-2">
                <!-- Table Top -->
                <b-row>
                    <!-- Per Page -->
                    <b-col md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
                        <label class="text-capitalize">{{ $t("table.show") }}</label>
                        <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                            :options="perPageOptions" :clearable="false"
                            class="per-page-selector d-inline-block mx-50" />
                        <label class="text-capitalize">{{ $t("table.entries") }}</label>
                    </b-col>
                    <!-- Search -->
                    <b-col cols="12" md="6">
                        <div class="d-flex align-items-center justify-content-end">
                            <b-form-input v-model="searchQuery" class="d-inline-block mr-1"
                                :placeholder="$t('table.search') + '...'" @keypress.enter="refetchData" />
                            <b-button variant="primary" @click="handleCreateFromScratch()">
                                <span class="text-nowrap text-capitalize">{{
                                    `${$t("actions.add")} ${$tc("dmp.template", 1)}`
                                    }}</span>
                            </b-button>
                        </div>
                    </b-col>
                </b-row>
            </div>

            <b-table ref="refListTable" class="position-relative" :items="fetchList" responsive :fields="tableColumns"
                primary-key="id" :sort-by.sync="sortBy" show-empty :empty-text="$t('no_record_found')"
                :sort-desc.sync="isSortDirDesc">
                <template #head()="data">
                    <span> {{ $t(data.label, 1) }}</span>
                </template>

                <!-- Column: Actions -->
                <template #cell(actions)="data">
                    <b-button :id="`actions-${data.item.id}`" tabindex="0" variant="white" class="px-1 btn-y">
                        <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
                    </b-button>
                    <b-popover :target="`actions-${data.item.id}`" triggers="focus" placement="bottom"
                        custom-class="popover-p-0" variant="white">
                        <b-list-group class="m-0">
                            <b-list-group-item class="border-0" button @click="handleEdit(data.item)">
                                <feather-icon icon="EditIcon" />
                                <span class="align-middle ml-50 text-capitalize">
                                    {{ $t("actions.edit") }}
                                </span>
                            </b-list-group-item>
                            <b-list-group-item class="border-0" button @click="confirmDelete(data.item.id)">
                                <feather-icon icon="TrashIcon" />
                                <span class="align-middle ml-50 text-capitalize">
                                    {{ $t("actions.delete") }}
                                </span>
                            </b-list-group-item>
                        </b-list-group>
                    </b-popover>
                </template>
            </b-table>

            <div class="mx-2 mb-2">
                <b-row>
                    <b-col cols="12" sm="6"
                        class="d-flex align-items-center justify-content-center justify-content-sm-start">
                        <span class="text-muted text-capitalize">{{
                            $t("table.showing", {
                                from: dataMeta.from,
                                to: dataMeta.to,
                                of: dataMeta.of,
                            })
                        }}</span>
                    </b-col>
                    <!-- Pagination -->
                    <b-col cols="12" sm="6"
                        class="d-flex align-items-center justify-content-center justify-content-sm-end">
                        <Pagination :totalList="totalList" :currentPage="currentPage" @updatePagination="update"
                            :perPage="perPage">
                        </Pagination>
                    </b-col>
                </b-row>
            </div>

        </b-card>

    </div>
</template>

<script>
import {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BButton,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, getCurrentInstance, computed } from "@vue/composition-api";
import useList from "./useList";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import Pagination from "@/components/Pagination.vue";
import Swal from "sweetalert2";

export default {
    components: {
        Pagination,
        BreadcrumbMenu,
        ToastificationLoadingContent,
        BCard,
        BRow,
        BCol,
        BFormInput,
        BButton,
        BTable,
        BDropdown,
        BDropdownItem,
        BPagination,
        BTooltip,
        vSelect,
    },

    setup(_, context) {
        //start pagination
        const update = (data) => {
            currentPage.value = data;
        };
        const { proxy } = getCurrentInstance();
        //end pagination

        const loadingToast = ref(null);
        const action = ref("list");
        const buttonExport = ref(false);
        const { t } = useI18nUtils();
        const toast = useToast();

        const trans = {
            "messages.delete.title": t("messages.delete.title"),
            "messages.delete.body": t("messages.delete.body"),
            "actions.delete": t("actions.delete"),
            "actions.cancel": t("actions.cancel"),
            "dmp.actions": t("dmp.actions"),
        };

        const confirmDelete = function (id) {
            const self = this;
            const storeInner = store;
            this.$swal({
                title: trans["messages.delete.title"],
                text: trans["messages.delete.body"],
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: trans["actions.delete"],
                cancelButtonText: trans["actions.cancel"],
                customClass: {
                    confirmButton: "btn btn-primary text-capitalize",
                    cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
                },
                buttonsStyling: false,
            }).then((result) => {
                if (result.value) {
                    storeInner
                        .dispatch("dmp_templates/delete", id)
                        .then((response) => {
                            if (response.success) {
                                toast({
                                    component: ToastificationContent,
                                    props: {
                                        title: response.message,
                                        icon: "CheckIcon",
                                        variant: "success",
                                    },
                                });
                                self.refetchData();
                            } else {
                                toast(
                                    {
                                        component: ToastificationContent,
                                        props: {
                                            title: response.message,
                                            icon: "AlertTriangleIcon",
                                            variant: "danger",
                                        },
                                    },
                                    {
                                        timeout: 10000,
                                    }
                                );
                            }
                        })
                        .catch((response) => {
                            toast(
                                {
                                    component: ToastificationContent,
                                    props: {
                                        title: response.response.data.message,
                                        icon: "AlertTriangleIcon",
                                        variant: "danger",
                                    },
                                },
                                {
                                    timeout: 10000,
                                }
                            );
                        });
                }
            });
        };

        const handleEdit = (item) => {
            const id = item.id;
            if (!id) return;
            const query = {};
            const account = item.summary.accountNumber;
            if (account) query.account = account;
            proxy.$router.push({ name: "dmp_templates_config", params: { id }, query });
        };

        const buildEmptySummary = () => {
            const accountNumber = "";

            return {
                areas: [],
                doors: [],
                model: "",
                users: [],
                status: 0,
                devices: [],
                outputs: [],
                dateTime: "",
                profiles: [],
                zones24h: [],
                macAddress: "",
                bell_options: {},
                card_formats: [],
                serialNumber: "",
                accountNumber,
                holiday_dates: {},
                area_schedules: [],
                output_groups: [],
                output_options: {},
                remote_options: [],
                system_report: {},
                system_options: {},
                time_schedules: [],
                menu_display: {},
                status_list: {},
                pc_log_report: {},
                network_options: {},
                zone_information: [],
                global_area_config: {},
                output_information: [],
                communication_paths: [],
            };
        };

        const handleCreateFromScratch = async () => {
            const result = await Swal.fire({
                title: proxy.$t("dmp.templateConfig.createTemplate"),
                icon: "info",
                showCancelButton: true,
                confirmButtonText: proxy.$t("dmp.templateConfig.createTemplate"),
                cancelButtonText: proxy.$t("actions.cancel"),
                customClass: {
                    confirmButton: "btn btn-primary text-capitalize",
                    cancelButton: "btn btn-outline-secondary ml-1",
                },
                html: `
          <input id="swal-tpl-name" class="swal2-input" placeholder="${proxy.$t(
                    "dmp.templateConfig.templateNamePlaceholder"
                )}" maxlength="150">
          <textarea id="swal-tpl-description" class="swal2-textarea" placeholder="${proxy.$t(
                    "dmp.templateConfig.templateDescriptionPlaceholder"
                )}" maxlength="150"></textarea>
        `,
                preConfirm: () => {
                    const name = document.getElementById("swal-tpl-name").value.trim();
                    const description = document
                        .getElementById("swal-tpl-description")
                        .value.trim();
                    if (!name) {
                        Swal.showValidationMessage(proxy.$t("dmp.templateConfig.nameRequired"));
                        return false;
                    }
                    return { name, description };
                },
                buttonsStyling: false,
            });

            if (!result.isConfirmed) return;

            const payload = {
                id_template: 0,
                template_name: result.value.name,
                description: result.value.description || " ",
                summary: buildEmptySummary(),
                config: {},
            };

            try {
                const response = await store.dispatch("dmp_templates/createTemplate", payload);

                if (response && response.success === false) {
                    throw new Error(
                        response.message || proxy.$t("dmp.templateConfig.createTemplateError")
                    );
                }

                toast({
                    component: ToastificationContent,
                    props: {
                        title: proxy.$t("dmp.templateConfig.createdCorrectly"),
                        icon: "CheckIcon",
                        variant: "success",
                    },
                });

                const newId =
                    (response &&
                        response.data &&
                        (response.data.id_template || response.data.id)) ||
                    (response && (response.id_template || response.id));

                if (newId) {
                    const query = {};
                    const account = 0;
                    if (account) query.account = account;
                    proxy.$router.push({ name: "dmp_templates_config", params: { id: newId }, query });
                }
            } catch (error) {
                const message =
                    (error.response && error.response.data && error.response.data.message) ||
                    error.message ||
                    proxy.$t("dmp.templateConfig.createTemplateError");
                toast({
                    component: ToastificationContent,
                    props: {
                        title: message,
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
            }
        };




        const {
            fetchList,
            tableColumns,
            perPage,
            currentPage,
            totalList,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refListTable,
            refetchData,
            startDate,
            endDate,
            activeFilter,
            buildFilters,
            clearSearchFilters,
            filtersCount,
        } = useList();

        return {
            confirmDelete,
            buttonExport,
            fetchList,
            tableColumns,
            perPage,
            currentPage,
            totalList,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refListTable,
            refetchData,
            activeFilter,
            startDate,
            endDate,
            loadingToast,
            action,
            update,
            filtersCount,
            buildFilters,
            clearSearchFilters,
            handleEdit,
            handleCreateFromScratch
        };
    },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.per-page-selector {
    width: 90px;
}
</style>
